import React from 'react';
import { useAuth } from '../contexts/AuthContext';

export type TripType = 'single-day' | 'multiple-days' | 'deep-planning' | null;

interface WelcomePageProps {
  onSelectTripType: (tripType: TripType) => void;
}

export const WelcomePage: React.FC<WelcomePageProps> = ({ onSelectTripType }) => {
  const { user } = useAuth();

  const getUserDisplayName = () => {
    if (user?.displayName) {
      return user.displayName.split(' ')[0]; // First name only
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'there';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col items-center justify-center p-4">
      {/* Logo and greeting */}
      <div className="mb-8 text-center">
        <div className="w-32 h-32 mx-auto mb-4 bg-gray-900 rounded-2xl overflow-hidden shadow-lg relative">
          {/* Crop to center 9/10 of image, rescale to same display size */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[111.11%] h-[111.11%]">
            <img
              src="/icons/LOGO1.jpg"
              onError={(e) => {
                const img = e.currentTarget;
                if (img) img.src = '/icons/LOGO.JPG';
              }}
              alt="RouteX"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome, {getUserDisplayName()}!</h1>
        <p className="text-lg text-gray-600">How can I help you today?</p>
      </div>

      {/* Trip type buttons */}
      <div className="w-full max-w-md space-y-4">
        {/* Single Day Trip */}
        <button
          onClick={() => onSelectTripType('single-day')}
          className="w-full bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] border border-gray-100 group"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-white text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                today
              </span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Single Day Trip</h3>
              <p className="text-gray-600 text-sm">Plan a quick route with stops for a single day adventure</p>
            </div>
            <span className="material-symbols-outlined text-gray-400 group-hover:text-blue-600 transition-colors">
              arrow_forward
            </span>
          </div>
        </button>

        {/* Multiple Days Trip */}
        <button
          onClick={() => {
            // Show coming soon message or do nothing
            // This button is disabled but we keep onClick for better UX handling
          }}
          disabled
          className="w-full bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all border border-gray-100 opacity-60 cursor-not-allowed relative group"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-white text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                calendar_month
              </span>
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-semibold text-gray-900">Multiple Days Trip <span className="text-gray-500 font-normal">(Pro)</span></h3>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Coming Soon</span>
              </div>
              <p className="text-gray-600 text-sm">Plan multi-day trips with overnight stays and detailed itineraries</p>
            </div>
            <span className="material-symbols-outlined text-gray-400">
              arrow_forward
            </span>
          </div>
        </button>

        {/* Deep Planning */}
        <button
          onClick={() => {
            // Show coming soon message or do nothing
            // This button is disabled but we keep onClick for better UX handling
          }}
          disabled
          className="w-full bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all border border-gray-100 opacity-60 cursor-not-allowed relative group"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-white text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                explore
              </span>
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-semibold text-gray-900">Deep Planning <span className="text-gray-500 font-normal">(Pre)</span></h3>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Coming Soon</span>
              </div>
              <p className="text-gray-600 text-sm">Get detailed, AI-powered route planning with comprehensive analysis</p>
            </div>
            <span className="material-symbols-outlined text-gray-400">
              arrow_forward
            </span>
          </div>
        </button>
      </div>

      {/* Helper text */}
      <p className="mt-8 text-gray-500 text-sm text-center max-w-md">
        Choose a planning mode to get started with your journey
      </p>
    </div>
  );
};
